import React, { useState, useEffect } from 'react'
import { Dispatch } from 'redux'
import { connect } from 'react-redux'

import Layout from '../layouts/layout'
import Wrapper from '../layouts/wrapper'
import { Item, itemsActions } from '../../core/items'
import { brandsActions, Brand, DEFAULT_BRAND_CODE } from '../../core/brands'
import { createEmptyItemTranslations } from '../../core/i18n'
import { LANGUAGE_CODES, SupportedLanguageCode } from '../../core/i18n'
import RootState from '../../core/root-state'
import ManufacturingInfoForm from '../components/new-item/manufacturing-info-form'
import { strings } from '../components/new-item/new-item-content'
import styles from '../styles/modules/new-item/new-item.module.scss'
import ItemInfoForm from '../components/new-item/item-info-form'
import Button from '../components/button'
import PageLanguageTabs from '../components/common/page-language-tabs'
import { BrandCode } from '../components/common'
import { roleType, Permission } from '../../core/auth0'
import withPermissionCheck from '../../core/access-control/with-permission-check'

interface Props {
  isLoading: boolean
  role: roleType
  brands: Brand[]
  createItem: (item: Item, brandCode?: string) => void
  fetchBrands: () => void
}

const NewItemScreen: React.FC<Props> = (props: Props) => {
  const [pageActiveLanguage, setPageActiveLanguage] = useState<
    SupportedLanguageCode
  >(LANGUAGE_CODES.JAPANESE)
  const [selectedBrand, setSelectedBrand] = useState<BrandCode>(
    DEFAULT_BRAND_CODE
  )
  const isAdmin = props.role === roleType.ADMIN

  useEffect(() => {
    props.fetchBrands()
    // ブランドリストの初期取得のため、コンポーネントマウント時に一度だけ実行される必要がある
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const [item, setItem] = useState<Item>({
    id: null,
    beanAmount: null,
    beanTypeIndex: 1,
    machineButtonIndex: 1,
    name: '',
    price: null,
    disposalSeconds: null,
    category: 'hot',
    cupType: 1,
    description: '',
    story: '',
    itemTranslations: createEmptyItemTranslations(),
    brand_code: isAdmin ? selectedBrand : undefined,
  })

  const isSavable = () => {
    const {
      beanAmount,
      beanTypeIndex,
      machineButtonIndex,
      name,
      price,
      disposalSeconds,
      category,
      cupType,
      description,
      story,
    } = item
    if (
      name === '' ||
      category === null ||
      description === '' ||
      story === ''
    ) {
      return false
    } else if (
      price === null ||
      price === 0 ||
      disposalSeconds === null ||
      disposalSeconds === 0 ||
      beanAmount === null ||
      beanAmount === 0 ||
      beanTypeIndex === null ||
      beanTypeIndex === 0 ||
      machineButtonIndex === null ||
      cupType === null
    ) {
      return false
    } else {
      return true
    }
  }

  const handleOnChangeManufacturingInfo = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const name = event.target.name
    const value = event.target.value
    if (
      name === 'machineButtonIndex' ||
      name === 'beanTypeIndex' ||
      name === 'beanAmount'
    ) {
      const re = /^[0-9\b]+$/
      if (value === '') {
        setItem({ ...item, [name]: null })
      }
      if (re.test(value)) {
        setItem({ ...item, [name]: Number(value) })
      }
    }
  }

  const handleOnChangeItemInfo = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const name = event.target.name
    const value = event.target.value

    // itemTranslations.en_US.name, itemTranslations.en_US.description, itemTranslations.en_US.story の形式に対応
    if (name.startsWith('itemTranslations.')) {
      const [, language, field] = name.split('.')
      const itemTranslations = [...item.itemTranslations]
      const langIndex = itemTranslations.findIndex(t => t.langCode === language)

      if (langIndex >= 0) {
        itemTranslations[langIndex] = {
          ...itemTranslations[langIndex],
          [field]: value,
        }
      } else {
        const newTranslation: any = {
          langCode: language,
          name: '',
          description: '',
          story: '',
        }
        newTranslation[field] = value
        itemTranslations.push(newTranslation)
      }

      setItem({
        ...item,
        itemTranslations,
      })
    } else if (
      name === 'name' ||
      name === 'category' ||
      name === 'description' ||
      name === 'story'
    ) {
      setItem({ ...item, [name]: value })
    } else if (
      name === 'price' ||
      name === 'disposalSeconds' ||
      name === 'cupType'
    ) {
      const re = /^[0-9\b]+$/
      if (value === '') {
        setItem({ ...item, [name]: null })
      }
      if (re.test(value)) {
        setItem({ ...item, [name]: Number(value) })
      }
    }
  }

  const handleOnPageLanguageChange = (language: SupportedLanguageCode) => {
    setPageActiveLanguage(language)
  }

  const handleBrandChange = (brand: BrandCode) => {
    setSelectedBrand(brand)
    setItem({ ...item, brand_code: brand })
  }

  const handleOnClickSave = () => {
    const itemToCreate = { ...item }
    const brandCodeToSend = isAdmin ? selectedBrand : undefined
    props.createItem(itemToCreate, brandCodeToSend)
    setItem({
      id: null,
      beanAmount: null,
      beanTypeIndex: 1,
      machineButtonIndex: 1,
      name: '',
      price: null,
      disposalSeconds: null,
      category: 'hot',
      cupType: 1,
      description: '',
      story: '',
      itemTranslations: createEmptyItemTranslations(),
      brand_code: isAdmin ? selectedBrand : undefined,
    })
  }

  return (
    <Layout location={'new_item'}>
      <Wrapper title="アイテム追加">
        <Button
          className={styles.itemSaveButton}
          title={strings.save}
          disabled={!isSavable()}
          onClick={handleOnClickSave}
        />
        {isAdmin && (
          <div className={styles.brandSelection}>
            <h3 className={styles.brandLabel}>ブランド選択</h3>
            <select
              className={styles.brandSelect}
              value={selectedBrand || ''}
              onChange={event =>
                handleBrandChange(event.target.value as BrandCode)
              }
            >
              {props.brands.map(brand => (
                <option key={brand.code} value={brand.code}>
                  {brand.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <PageLanguageTabs
          activeLanguage={pageActiveLanguage}
          onLanguageChange={handleOnPageLanguageChange}
        />
        <h3 className={styles.title}>{strings.manufacturingInfo}</h3>
        <ManufacturingInfoForm
          item={item}
          activeLanguage={pageActiveLanguage}
          onChange={handleOnChangeManufacturingInfo}
        />
        <h3 className={styles.title}>{strings.itemInfo}</h3>
        <ItemInfoForm
          item={item}
          activeLanguage={pageActiveLanguage}
          onChange={handleOnChangeItemInfo}
        />
      </Wrapper>
    </Layout>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    isLoading: state.items.isLoading,
    role: state.auth0.role,
    brands: state.brands.brands,
  }
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  createItem: (item: Item, brandCode?: string) =>
    dispatch(itemsActions.createItem(item, brandCode)),
  fetchBrands: () => dispatch(brandsActions.fetchBrands()),
})

const ConnectedComponent = connect(mapStateToProps, mapDispatchToProps)(NewItemScreen)

export default withPermissionCheck(ConnectedComponent, {
  requiredPermission: Permission.ITEM_CREATE,
  redirectTo: '/404'
})
